import React, { useEffect } from 'react';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  TagIcon,
  UsersIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

import { useDispatch, useSelector } from 'react-redux';
import {
  thunkFetchCouponAnalytics,
  thunkFetchMostUsedCoupons,
  thunkDownloadCouponReport,
} from '../../redux/slices/couponSlice';
import { useAdminLayout } from "../../context/adminLayoutUseContext";
const CouponAnalytics = () => {
  const dispatch = useDispatch();
  const {
    analytics,
    mostUsedCoupons,
    loading,
    error,
    reportBlob,
  } = useSelector((state) => state.coupon);
  const [selectedPeriod, setSelectedPeriod] = React.useState('30');
  const { setLayoutConfig } = useAdminLayout();

  // Fetch analytics and most used coupons when selectedPeriod changes
  useEffect(() => {
    dispatch(thunkFetchCouponAnalytics(selectedPeriod));
    dispatch(thunkFetchMostUsedCoupons());
  }, [dispatch, selectedPeriod]);


  
      useEffect(() => {
        setLayoutConfig({
          title: "Coupon / Coupon Analytics",
          showBackButton: true,
          scrollable: true,
        });
       
      }, [dispatch,  setLayoutConfig]);
  // Download report for the selected period
  const downloadReport = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - parseInt(selectedPeriod));
    // Format dates as YYYY-MM-DD
    const format = (d) => d.toISOString().slice(0, 10);
    console.log('Downloading report from', format(startDate), 'to', format(endDate));
    dispatch(thunkDownloadCouponReport({ startDate: format(startDate), endDate: format(endDate) }));
  };

  // Handle report download when blob is ready
  useEffect(() => {
    if (reportBlob) {
      const url = window.URL.createObjectURL(new Blob([reportBlob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coupon_report_${selectedPeriod}days.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }, [reportBlob, selectedPeriod]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (error) {
    return <div>Error: {error}</div>;
  }
  console.log('Analytics Data:', analytics);
  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Coupon Analytics</h1>
            <p className="text-gray-600 mt-2">Performance insights and usage statistics</p>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>

            <button
              onClick={downloadReport}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download Report
            </button>
          </div>
        </div>
      </div>
      
      {analytics && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TagIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Coupons</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.total_coupons}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ChartBarIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Coupons</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.active_coupons}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <UsersIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Uses</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.total_uses}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Discount</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${parseFloat(analytics.total_discount_given).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Expired Coupons</span>
                  <span className="font-medium">{analytics.expired_coupons}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Orders Affected</span>
                  <span className="font-medium">{analytics.total_orders_affected}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg Discount/Use</span>
                  <span className="font-medium">
                    ${parseFloat(analytics.average_discount_per_use).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Most Used</span>
                  <span className="font-medium text-blue-600">{analytics.most_used_coupon}</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage by Type</h3>
              <div className="space-y-3">
                {Object.entries(analytics.usage_by_type).map(([type, count]) => (
                  <div key={type} className="flex justify-between items-center">
                    <span className="text-gray-600">{type}</span>
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${(count / Math.max(...Object.values(analytics.usage_by_type))) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="font-medium w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Discount by Type</h3>
              <div className="space-y-3">
                {Object.entries(analytics.discount_by_type).map(([type, amount]) => (
                  <div key={type} className="flex justify-between items-center">
                    <span className="text-gray-600">{type}</span>
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{
                            width: `${(amount / Math.max(...Object.values(analytics.discount_by_type))) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="font-medium w-16 text-right">${amount.toFixed(0)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Most Used Coupons */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Most Used Coupons</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uses
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {mostUsedCoupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 font-mono">
                          {coupon.code}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{coupon.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {coupon.coupon_type_display}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {coupon.current_uses}
                        {coupon.max_uses && ` / ${coupon.max_uses}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${parseFloat(coupon.total_discount_given).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${coupon.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : coupon.is_expired
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}>
                          {coupon.is_expired ? 'Expired' : coupon.status_display}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Monthly Usage Trends</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {Object.entries(analytics.usage_by_month).slice(-6).map(([month, uses]) => (
                  <div key={month} className="text-center">
                    <div className="text-xs text-gray-500 mb-1">
                      {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{uses}</div>
                    <div className="text-xs text-gray-500">
                      ${(analytics.discount_by_month[month] || 0).toFixed(0)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CouponAnalytics;